























import { container } from 'tsyringe';
import { onActivated, onDeactivated, onMounted, onUnmounted, defineComponent, ref, Ref, watch, computed } from '@vue/composition-api';
import { getReactiveStory, isInEditor, pingEditor } from '@/storyblok';
import ViewContainer from '@/components/ViewContainer.vue';
import LoadingAnimation from '@/components/LoadingAnimation.vue';
import TextBubble from '@/components/TextBubble.vue';
import Scanner from '@/components/Scanner.vue';
import { ModelManager } from '@/prototyping/model';
import { messenger } from '@/messenger';
import { JsonRpcMessage } from '@/messenger/jrpc/message';
import { ChannelSubscription } from '@/messenger/core/channel';

const routeName = 'ARImageTracking';

export default defineComponent({
  components: { ViewContainer, LoadingAnimation, TextBubble, Scanner },
  name: routeName,
  setup() {
    const loaded = ref(false);
    const tracking = ref(false);
    const showPlay = ref(false);
    const inEditor = ref(false);
    const story: Ref<any> = ref(null);

    const scannerProps = {
      viewWidth: computed(() => story.value?.story?.content?.viewWidth),
      bgEnabled: computed(() => story.value?.story?.content?.bgEnabled),
      bgZIndex: computed(() => Number(story.value?.story?.content?.bgZIndex)),
      bgColor: computed(() => story.value?.story?.content?.bgColor?.color),
      bgBlur: computed(() => story.value?.story?.content?.bgBlur),
      borderEnabled: computed(() => story.value?.story?.content?.borderEnabled),
      borderZIndex: computed(() => Number(story.value?.story?.content?.borderZIndex)),
      borderColor: computed(() => story.value?.story?.content?.borderColor?.color),
      borderWidth: computed(() => story.value?.story?.content?.borderWidth),
    }

    let imageDetectedSubscription: undefined | ChannelSubscription;
    let trackingLostSubscription: undefined | ChannelSubscription;

    async function enableARandImageTracking() {
      try {
        await messenger.unity?.scene.initializeAR();
        await messenger.unity?.scene.loadScene('ARImageTrackingScene');
      } catch (e) {
        console.error(e);
      }
    }

    async function disableARandImageTracking() {
      try {
        await messenger.unity?.scene.loadScene('VinylPostcardsLoadingScene');
        await messenger.unity?.scene.deinitializeAR();
      } catch (e) {
        console.error(e);
      }
    }

    // fetch arimagetracking story
    async function fetchStory() {
      [, story.value] = await getReactiveStory(`${routeName.toLowerCase()}`);
    }

    function callbackFoundImageTarget(rpcMessageWithImageTitle: JsonRpcMessage) {
      tracking.value = true;
      console.log(`foundImageTarget ${(rpcMessageWithImageTitle.params as Array<string>)[0]}`);
    }

    function callbackTrackingLost() {
      tracking.value = false;
      console.log(`trackingLost`);
    }

    async function checkIsInEditor() {
      await pingEditor();
      inEditor.value = isInEditor();
    }

    async function initScene() {
      imageDetectedSubscription = messenger.unity?.ar.channel.subscribe('foundImageTarget', callbackFoundImageTarget);
      trackingLostSubscription = messenger.unity?.vinylPostcards.channel.subscribe('trackingLost', callbackTrackingLost);
      await fetchStory();
      await checkIsInEditor();
      await enableARandImageTracking();
      loaded.value = true;
    }

    function unloadScene() {
      if (loaded.value) {
        loaded.value = false;
        imageDetectedSubscription?.unsubscribe();
        trackingLostSubscription?.unsubscribe();
        disableARandImageTracking()
          .then(() => {
            tracking.value = false;
          })
          .catch(e => console.log(e));
      }
    }

    onActivated(initScene);
    onMounted(initScene);

    onDeactivated(unloadScene);
    onUnmounted(unloadScene);

    const modelManager = container.resolve(ModelManager);
    const audioPlayer = modelManager.get('audio-player', 'AudioPlayerModel');

    watch(audioPlayer.isPlaying, async () => {
      try {
        if (audioPlayer.isPlaying.value !== undefined) await messenger.unity?.vinylPostcards.changePlayingState(audioPlayer.isPlaying.value);
      } catch (e) {
        console.error(e);
      }
    });

    watch(audioPlayer.currentAudioTrack, async (val, oldVal) => {
      try {
        if (val?.src !== oldVal?.src && val !== undefined) await messenger.unity?.vinylPostcards.changeAudioCover(val.src as string);
      } catch (e) {
        console.error(e);
      }
    });

    watch(tracking, async (val) => {
      try {
        showPlay.value = false;
        if (!val) return;
        setTimeout(() => {
          showPlay.value = true;
        }, 7*1000);
      } catch (e) {
        console.error(e);
      }
    });

    fetchStory();

    audioPlayer.stop();

    return {
      loaded,
      tracking,
      showPlay,
      inEditor,
      isPlaying: audioPlayer.isPlaying,
      togglePlay: audioPlayer.togglePlay,
      ...scannerProps
    };
  },
});
